import { Button, type ButtonProps } from "@radix-ui/themes";
import type { UpgradeSurface } from "../../../../utils/conversion-shared";
import { useUsage } from "../../contexts/UsageContext";
import { getCheckoutUrl } from "../../utils/billingUrls";
import { trackUpgradeClicked } from "../../utils/conversionAnalytics";
import { useUpgradeAccountCheck } from "../billing/UpgradeAccountCheck";

type UpgradeFormProps = Omit<ButtonProps, "type" | "disabled"> & {
    surface: UpgradeSurface;
};

/**
 * "Upgrade to Pro" button that posts to Stripe checkout. Children render before the label, e.g. an
 * icon. checkoutStarted is shared app-wide: the sidebar's button is visible next to each page's
 * own, so a click on one has to disable all of them. An account that shows no sign of use is asked
 * "Is this the right account?" first (see useUpgradeAccountCheck).
 */
export default function UpgradeForm({ surface, children, ...buttonProps }: UpgradeFormProps) {
    const { checkoutStarted, setCheckoutStarted, conversionContext } = useUsage();
    const accountCheck = useUpgradeAccountCheck(surface);

    return (
        <>
            <form
                action={getCheckoutUrl(surface)}
                method="post"
                style={{
                    flex: "none",
                }}
                onSubmit={(event) => {
                    if (accountCheck.holdCheckout(event)) {
                        return;
                    }
                    void trackUpgradeClicked({
                        surface,
                        conversionContext,
                        fallbackPlan: "free",
                    });
                    setCheckoutStarted(true);
                }}
            >
                <Button type="submit" disabled={checkoutStarted} {...buttonProps}>
                    {children}
                    {checkoutStarted ? "Opening checkout…" : "Upgrade to Pro"}
                </Button>
            </form>
            {accountCheck.dialog}
        </>
    );
}
